import onetime from "@-/com.npmjs.onetime";
import { createAsyncStoragePersister } from "@-/com.npmjs.tanstack__query-async-storage-persister";
import {
  experimental_createQueryPersister,
  type PersistedClient,
} from "@-/com.npmjs.tanstack__react-query-persist-client";
import {
  LEGACY_QUERY_CACHE_KEY,
  MUTATION_CACHE_KEY,
  QUERY_CACHE_BUSTER,
  QUERY_CACHE_MAX_AGE,
  STATIC_QUERY_CACHE_METADATA_KEY,
  STATIC_QUERY_CACHE_PREFIX,
} from "#query-cache-policy";

export type QueryCacheStorage = {
  getItem: (key: string) => string | null;
  getKeys: () => string[];
  removeItem: (key: string) => void;
  setItem: (key: string, value: string) => void;
};

/**
 * Disk retention is independent of QueryClient GC. A cold launch restores only paused
 * mutations; each static query reads its own disk entry when it is actually requested.
 */
export function createQueryCachePersisters({
  buster = QUERY_CACHE_BUSTER,
  maxAge = QUERY_CACHE_MAX_AGE,
  storage,
  throttleTime = 1000,
}: {
  buster?: string;
  maxAge?: number;
  storage: QueryCacheStorage;
  throttleTime?: number;
}) {
  function isCurrent(timestamp: number, cachedBuster: string): boolean {
    return (
      Number.isFinite(timestamp) &&
      timestamp > 0 &&
      Date.now() - timestamp <= maxAge &&
      cachedBuster === buster
    );
  }

  function writeStaticMetadata(timestamp: number): void {
    storage.setItem(STATIC_QUERY_CACHE_METADATA_KEY, JSON.stringify({ buster, timestamp }));
  }

  function ensureStaticCacheIsCurrent(): boolean {
    const metadata = storage.getItem(STATIC_QUERY_CACHE_METADATA_KEY);
    if (metadata !== null) {
      try {
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- this private envelope is written below; fields are validated before use
        const parsed = JSON.parse(metadata) as { buster: string; timestamp: number };
        if (isCurrent(parsed.timestamp, parsed.buster)) {
          return true;
        }
      } catch {
        // An unreadable expiry envelope cannot make old static data valid again.
      }
    }

    for (const key of storage.getKeys()) {
      if (key.startsWith(`${STATIC_QUERY_CACHE_PREFIX}-`)) {
        storage.removeItem(key);
      }
    }
    storage.removeItem(STATIC_QUERY_CACHE_METADATA_KEY);
    return false;
  }

  // A pre-upgrade snapshot may contain both static queries and paused mutations. Parse it
  // once on the first upgraded launch, move its entries to disk, and return no static data
  // to hydrate(). The temporary parsed tree dies here. Future launches never read it.
  // onetime only records a successful return: a failed storage write leaves the legacy
  // snapshot intact and the next attempt retries the migration.
  const migrateLegacyCache = onetime(() => {
    const serialized = storage.getItem(LEGACY_QUERY_CACHE_KEY);
    if (serialized === null) {
      return;
    }
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- legacy format is the PersistedClient JSON written by TanStack's storage persister
    const legacy = JSON.parse(serialized) as PersistedClient;
    if (!isCurrent(legacy.timestamp, legacy.buster)) {
      storage.removeItem(LEGACY_QUERY_CACHE_KEY);
      return;
    }

    for (const query of legacy.clientState.queries) {
      if (
        query.meta?.persist !== true ||
        query.state.status !== "success" ||
        query.queryKey[0] !== "staticQuery"
      ) {
        continue;
      }
      storage.setItem(
        `${STATIC_QUERY_CACHE_PREFIX}-${query.queryHash}`,
        JSON.stringify({
          buster,
          queryHash: query.queryHash,
          queryKey: query.queryKey,
          state: query.state,
        }),
      );
    }
    // Preserve the snapshot's age, not each query's dataUpdatedAt. Previously any cache
    // update refreshed the whole snapshot, even when its immutable data was much older.
    writeStaticMetadata(legacy.timestamp);
    if (storage.getItem(MUTATION_CACHE_KEY) === null) {
      storage.setItem(
        MUTATION_CACHE_KEY,
        JSON.stringify({
          buster,
          clientState: { mutations: legacy.clientState.mutations, queries: [] },
          timestamp: legacy.timestamp,
        } satisfies PersistedClient),
      );
    }
    // Remove only after every write succeeds, including the separate mutation snapshot.
    storage.removeItem(LEGACY_QUERY_CACHE_KEY);
  });

  const mutationPersister = createAsyncStoragePersister({
    key: MUTATION_CACHE_KEY,
    storage: {
      getItem: (key) => {
        migrateLegacyCache();
        ensureStaticCacheIsCurrent();
        return storage.getItem(key);
      },
      removeItem: storage.removeItem,
      setItem: (key, value) => {
        migrateLegacyCache();
        // Root persistence still receives cache events. Updating this tiny envelope
        // preserves the former whole-snapshot expiry policy without serializing queries.
        ensureStaticCacheIsCurrent();
        writeStaticMetadata(Date.now());
        storage.setItem(key, value);
      },
    },
    throttleTime,
  });

  const staticPersister = experimental_createQueryPersister({
    buster,
    // Expiry is checked by the shared snapshot envelope, preserving the existing 24h
    // policy and each query's original freshness timestamp across migration/restoration.
    maxAge: Number.POSITIVE_INFINITY,
    prefix: STATIC_QUERY_CACHE_PREFIX,
    refetchOnRestore: false,
    storage: {
      getItem: (key) => {
        migrateLegacyCache();
        return ensureStaticCacheIsCurrent() ? storage.getItem(key) : null;
      },
      removeItem: storage.removeItem,
      setItem: (key, value) => {
        migrateLegacyCache();
        ensureStaticCacheIsCurrent();
        storage.setItem(key, value);
        writeStaticMetadata(Date.now());
      },
    },
  });

  return {
    mutationPersister,
    staticQueryPersister: staticPersister.persisterFn,
  };
}
